import type { DeviceModel } from '../../types/deviceModel';
import { ENDPOINT_TYPES_DEFAULT_EXCLUDED } from '../../types/deviceModel';
import type { PlacedEquipment, PortRect, RackEquipment, SimpleLayout } from '../../types';
import { makeTemplateForPorts } from '../../utils/layoutTemplates';
import { getCatalogDeviceModel } from '../../data/deviceModels';
import { collectLayoutPorts, type FacedPort } from './layoutPorts';
import { applyPortKindChange } from './validateDeviceModel';
import { stripDisabledRolePoe } from './electricalGraph';

export interface ResolvedDevice {
  nodeId: string;
  catalogId: string;
  name: string;
  type: string;
  model: DeviceModel;
  unmodeled: boolean;
  excluded: boolean;
  config?: PlacedEquipment['config'];
  ports: FacedPort[];
  rackId?: string;
  instanceId?: string;
}

export function isDefaultExcludedType(type?: string): boolean {
  return ENDPOINT_TYPES_DEFAULT_EXCLUDED.has(String(type || ''));
}

export function isLogicExcluded(
  config: { logicExcluded?: boolean; poweredOff?: boolean } | undefined,
  type?: string
): boolean {
  if (config?.poweredOff) return true;
  if (typeof config?.logicExcluded === 'boolean') return config.logicExcluded;
  return isDefaultExcludedType(type);
}

export function inferModelFromLayout(
  catalogId: string,
  type: string,
  layout?: { simple?: SimpleLayout; multiFace?: any }
): DeviceModel {
  let ports = collectLayoutPorts(layout);
  if (ports.length === 0) {
    const count = 1;
    const simple = makeTemplateForPorts(count);
    ports = collectLayoutPorts({ simple });
  }
  const inferredLayout = layout?.multiFace
    ? { multiFace: layout.multiFace }
    : {
        simple: layout?.simple || {
          stage: { width: 260, height: 70 },
          ports: ports.map((p) => {
            const next = { ...p } as PortRect & { face?: string };
            delete next.face;
            return next;
          }),
        },
      };

  const isPatch = type === 'patch-panel';
  const isSolar = type === 'solar-panel';
  const isBattery = type === 'battery';
  const isInverter = type === 'inverter';
  const patchedPorts = ports.map((p) => {
    if (isPatch && p.kind === 'rj45') return { ...p, passthrough: true };
    return applyPortKindChange(p, String(p.kind));
  });

  return {
    equipmentId: catalogId,
    selfDrawWatts: isPatch || type === 'psu' || isSolar || isBattery ? 0 : 5,
    powerInputs: isPatch || isSolar
      ? []
      : isBattery
        ? [{ id: 'bat', kind: 'battery', voltageMin: 40, voltageMax: 60, plusPortNumber: patchedPorts.find((p) => String(p.kind) === 'batPlus')?.number, minusPortNumber: patchedPorts.find((p) => String(p.kind) === 'batMinus')?.number }]
        : type === 'psu'
        ? [{ id: 'ac', kind: 'ac', maxWatts: 60 }]
        : [{ id: 'ac', kind: 'ac', maxWatts: 60 }],
    isPassthroughDevice: isPatch,
    isPsu: type === 'psu',
    isSolar,
    isBattery,
    isInverter,
    layout: inferredLayout.multiFace
      ? { multiFace: inferredLayout.multiFace }
      : {
          simple: {
            stage: inferredLayout.simple!.stage,
            ports: patchedPorts.map((p) => {
              const next = { ...p } as PortRect & { face?: string };
              delete next.face;
              return next;
            }),
          },
        },
  };
}

export function layoutFromPlaced(eq: PlacedEquipment | { config?: PlacedEquipment['config']; equipment: { id: string; specifications?: any } }) {
  return eq.config?.layout;
}

export function resolveDeviceModel(
  catalogId: string,
  type: string,
  config?: PlacedEquipment['config']
): { model: DeviceModel; unmodeled: boolean } {
  if (config?.deviceModel) {
    return {
      model: {
        ...config.deviceModel,
        equipmentId: config.deviceModel.equipmentId || catalogId,
      },
      unmodeled: false,
    };
  }
  const catalog = getCatalogDeviceModel(catalogId);
  if (catalog) return { model: catalog, unmodeled: false };
  return {
    model: inferModelFromLayout(catalogId, type, config?.layout),
    unmodeled: true,
  };
}

export function resolvedPorts(model: DeviceModel, config?: PlacedEquipment['config']): FacedPort[] {
  const fromInstance = collectLayoutPorts(config?.layout);
  const fromModel = collectLayoutPorts(model.layout);
  if (fromInstance.length === 0) return fromModel;
  return fromInstance.map((p) => {
    if (p.poeIn || p.poeOut || p.passthrough) return p;
    const match =
      fromModel.find((m) => m.number === p.number && String(m.kind) === String(p.kind) && m.face === p.face) ||
      fromModel.find((m) => m.number === p.number && String(m.kind) === String(p.kind));
    if (!match || (!match.poeIn && !match.poeOut && !match.passthrough)) return p;
    return {
      ...p,
      poeIn: match.poeIn,
      poeOut: match.poeOut,
      passthrough: match.passthrough,
      speedsMbps: p.speedsMbps?.length ? p.speedsMbps : match.speedsMbps,
    };
  });
}

export function resolvePlacedEquipment(eq: PlacedEquipment): ResolvedDevice {
  const catalogId = eq.equipment.id;
  const { model, unmodeled } = resolveDeviceModel(catalogId, eq.equipment.type, eq.config);
  return {
    nodeId: eq.id,
    catalogId,
    name: eq.config?.label || eq.equipment.name,
    type: eq.equipment.type,
    model,
    unmodeled,
    excluded: isLogicExcluded(eq.config, eq.equipment.type),
    config: eq.config,
    ports: stripDisabledRolePoe(resolvedPorts(model, eq.config), model.portRoles),
  };
}

export function rackNodeId(rackId: string, item: RackEquipment, index: number): string {
  return `${rackId}:${item.equipment.id}:${index}`;
}

export function resolveRackEquipment(rackId: string, item: RackEquipment, index: number): ResolvedDevice {
  const catalogId = item.equipment.id;
  const { model, unmodeled } = resolveDeviceModel(catalogId, item.equipment.type, item.config);
  return {
    nodeId: rackNodeId(rackId, item, index),
    catalogId,
    name: item.config?.label || item.equipment.name,
    type: item.equipment.type,
    model,
    unmodeled,
    excluded: isLogicExcluded(item.config, item.equipment.type),
    config: item.config,
    ports: stripDisabledRolePoe(resolvedPorts(model, item.config), model.portRoles),
    rackId,
    instanceId: item.instanceId,
  };
}
